import React, { useEffect, useState } from 'react';
import NotificationStage from '../components/NotificationStage';
import notificationService from '../services/notificationService';
import { parseUrlParams } from '../utils/urlParams';
import { Notification } from '../types';

const Notifications = () => {
  const [notificationSettings, setNotificationSettings] = useState({
    enabled: true,
    interval: 15,
    animationEnabled: true,
    soundEnabled: false,
    theme: 'dark' as 'light' | 'dark',
    donationTextTemplate: '{{sender}} donated {{amount}} for {{challenge}}',
    challengeTextTemplate: '{{sender}} challenged you to {{challenge}}',
  });
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null);

  // Load notification settings from URL parameters or localStorage
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const urlParams = await parseUrlParams();
        
        // Get notification settings from URL params or localStorage
        const settings = {
          enabled: urlParams.notifications_enabled !== undefined 
            ? urlParams.notifications_enabled === 'true' 
            : notificationService.getSettings().enabled,
          interval: urlParams.notifications_interval 
            ? parseInt(urlParams.notifications_interval, 10)
            : notificationService.getSettings().interval,
          animationEnabled: urlParams.notifications_animation !== undefined
            ? urlParams.notifications_animation === 'true'
            : notificationService.getSettings().animationEnabled,
          soundEnabled: urlParams.notifications_sound !== undefined
            ? urlParams.notifications_sound === 'true'
            : notificationService.getSettings().soundEnabled,
          theme: (urlParams.notifications_theme === 'light' || urlParams.notifications_theme === 'dark'
            ? urlParams.notifications_theme
            : notificationService.getSettings().theme) as 'light' | 'dark',
          donationTextTemplate: urlParams.notifications_donation_text || notificationService.getSettings().donationTextTemplate,
          challengeTextTemplate: urlParams.notifications_challenge_text || notificationService.getSettings().challengeTextTemplate,
        };

        setNotificationSettings(settings);
        
        // Update notification service with these settings
        notificationService.setSettings(settings);
        
        // Store settings in localStorage for persistence
        localStorage.setItem('notification_settings', JSON.stringify(settings));
        
        // Set up notification listener
        const handleNewNotification = (notification: Notification) => {
          setCurrentNotification(notification);
        };
        
        notificationService.setOnNewNotification(handleNewNotification);
        
        // Start notification polling if enabled
        if (settings.enabled) {
          notificationService.startPolling();
        }
        
      } catch (error) {
        console.error('Failed to load notification settings:', error);
        // Fallback to default settings
        const defaultSettings = notificationService.getSettings();
        setNotificationSettings(defaultSettings);
      }
    };

    loadSettings();
    
    // Cleanup on unmount
    return () => {
      notificationService.stopPolling();
    };
  }, []);

  if (!currentNotification) {
    return null;
  }

  return (
    <NotificationStage
      notification={currentNotification}
      onClose={() => setCurrentNotification(null)}
      theme={notificationSettings.theme}
      donationTextTemplate={notificationSettings.donationTextTemplate}
      challengeTextTemplate={notificationSettings.challengeTextTemplate}
    />
  );
};

export default Notifications;
